import React, {useCallback, useState} from 'react';
import {Button, EMPHASIS, FlexRow, Icon, Label, Text, TextArea} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {ClientPrimaryContactDropdown} from '../../../forecast-app/shared/components/dropdowns/client-primary-contact-dropdown/ClientPrimaryContactDropdown';
import {MAX_REASON_LENGTH, QuoteDecision, recordQuoteDecision} from './recordQuoteDecision';
import {DecisionPanel, Emphasis, FieldRow, ReasonFieldRow} from './QuoteDecisionPanel.styled';

export type QuoteDecisionPanelProps = {
	/** Relay global ID of the quote being decided. */
	quoteId: string;
	/**
	 * Relay global ID of the project's client, or null when the project has no client. Passed
	 * straight into the picker's `company.client(id:)` query, which is why it is the global ID and
	 * not a numeric id.
	 */
	clientId: string | null;
	decision: QuoteDecision;
	/** Closes the panel and returns the card to its action row. */
	onCancel: () => void;
	/** Called after a successful decision, so the caller can refetch or track. */
	onDecided?: () => void;
};

type Contact = {id: string; name: string};

/**
 * Id for the Confirm button's label text, so the button can point `aria-labelledby` at it.
 *
 * BaseButton derives the accessible name from `children.toString()` — the literal "[object Object]"
 * for the JSX child a coloured icon requires — and applies it after spreading the caller's props, so
 * an `aria-label` of ours would be overwritten. `aria-labelledby` survives the spread and outranks
 * `aria-label` when the name is computed. Static because only one panel is ever open at a time.
 */
const CONFIRM_LABEL_ID = 'quote-decision-confirm-label';

/** Per-decision copy and identifiers. The only thing that differs between the three decisions. */
const COPY = {
	APPROVED: {
		dataCy: 'approve-quote-panel',
		titleId: 'quote_activity.approve_modal.title',
		descriptionId: null,
		confirmId: 'quote_activity.approve_confirm',
		noClientId: 'quote_activity.approve_modal.no_client',
		noContactsId: 'quote_activity.approve_modal.no_contacts',
		reasonLabelId: 'quote_activity.decline_modal.reason_label',
		reasonPlaceholderId: 'quote_activity.decline_modal.reason_placeholder',
		reasonDataCy: 'quote-decline-reason',
		icon: 'checkCircle',
		iconColor: 'success',
		showsReason: false,
		showsContact: true,
	},
	DECLINED: {
		dataCy: 'decline-quote-panel',
		titleId: 'quote_activity.decline_modal.title',
		descriptionId: null,
		confirmId: 'quote_activity.decline_confirm',
		noClientId: 'quote_activity.decline_modal.no_client',
		noContactsId: 'quote_activity.decline_modal.no_contacts',
		reasonLabelId: 'quote_activity.decline_modal.reason_label',
		reasonPlaceholderId: 'quote_activity.decline_modal.reason_placeholder',
		reasonDataCy: 'quote-decline-reason',
		icon: 'closeCircle',
		iconColor: 'error',
		showsReason: true,
		showsContact: true,
	},
	// The contact-less decision. showsContact false skips the picker AND the whole no-client /
	// no-contacts chain: those are contact-picker preconditions, and a revision has none — so a
	// revise on a project with no client must still confirm.
	//
	// It is also the only decision carrying a description line: unlocking the Baseline is a
	// consequence its two siblings do not have, so the panel spells it out before the user commits.
	REVISING: {
		dataCy: 'revise-quote-panel',
		titleId: 'quote_activity.revise_modal.title',
		descriptionId: 'quote_activity.revise_modal.description',
		confirmId: 'quote_activity.revise_confirm',
		// Never read — blockedMessageId short-circuits on showsContact — but the entries must share
		// a shape for the `as const` union to stay narrow. Pointed at the title so a stray render
		// would show something sane rather than warn about a missing key.
		noClientId: 'quote_activity.revise_modal.title',
		noContactsId: 'quote_activity.revise_modal.title',
		reasonLabelId: 'quote_activity.revise_modal.reason_label',
		reasonPlaceholderId: 'quote_activity.revise_modal.reason_placeholder',
		reasonDataCy: 'quote-revise-reason',
		icon: 'edit',
		iconColor: 'success',
		showsReason: true,
		showsContact: false,
	},
} as const;

/**
 * The decision form, rendered inline inside the Quote Activity card rather than as a modal, so
 * recording an outcome keeps the log it relates to on screen.
 *
 * One component for all three decisions. They differ only by title, confirm label, whether the
 * reason textarea and the contact picker render, which blocked-state copy is used, and the
 * decision sent — so the state matrix lives here exactly once instead of drifting across three
 * near-identical files.
 */
export const QuoteDecisionPanel = ({
	quoteId,
	clientId,
	decision,
	onCancel,
	onDecided,
}: QuoteDecisionPanelProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const copy = COPY[decision];
	const [contact, setContact] = useState<Contact | null>(null);
	const [reason, setReason] = useState('');
	const [submitting, setSubmitting] = useState(false);
	// The picker owns the contacts fetch, so it is the only thing that can tell us the client has
	// none — which is what separates "client with no contacts" from "client with contacts".
	const [clientHasNoContacts, setClientHasNoContacts] = useState(false);

	const hasClient = !!clientId;
	// Confirm is NEVER enabled without a contact for a decision that carries one: the contacts
	// module is on for every customer, so every such decision the UI submits carries one. A revise
	// carries none, so it drops out of the gate entirely rather than being blocked by it.
	const canConfirm = (!copy.showsContact || (hasClient && !!contact)) && !submitting;

	// Contact-picker logic, skipped whole when there is no picker.
	const blockedMessageId = !copy.showsContact
		? null
		: !hasClient
		? copy.noClientId
		: clientHasNoContacts
		? copy.noContactsId
		: null;
	// Confirm is disabled until a contact is chosen, so say which of the three reasons it is rather
	// than leaving a dead button with no explanation.
	const confirmHint = blockedMessageId
		? formatMessage({id: blockedMessageId})
		: !copy.showsContact || contact
		? undefined
		: formatMessage({id: 'quote_activity.select_contact_first'});

	const handleConfirm = useCallback(() => {
		if (copy.showsContact && !contact) return;
		setSubmitting(true);
		recordQuoteDecision(
			{
				quoteId,
				decision,
				clientContactId: copy.showsContact && contact ? contact.id : null,
				reason: copy.showsReason ? reason : null,
			},
			() => {
				if (onDecided) onDecided();
				onCancel();
			},
			() => {
				// Stay open on failure so the user does not retype the reason. Util.CommitMutation
				// has already surfaced the readable error modal.
				setSubmitting(false);
			}
		);
	}, [contact, quoteId, decision, reason, copy.showsReason, copy.showsContact, onDecided, onCancel]);

	return (
		<DecisionPanel data-cy={copy.dataCy}>
			<Label>{formatMessage({id: copy.titleId})}</Label>

			{/* Only Revise carries one: it is the only decision whose consequence — an unlocked
			    Baseline, and a new version filed on save — is not evident from the button alone. */}
			{copy.descriptionId && (
				<Text size="1" color="auxiliary" data-cy="revise-quote-description">
					{/* FormattedMessage rather than formatMessage: the sentence emphasises "Files",
					    which is markup the string form cannot carry. <b> is the repo's existing tag
					    for this, so translators see a familiar shape. */}
					<FormattedMessage
						id={copy.descriptionId}
						values={{b: (chunks: React.ReactNode) => <Emphasis>{chunks}</Emphasis>}}
					/>
				</Text>
			)}

			{copy.showsContact && hasClient && (
				<FieldRow>
					<Label>{formatMessage({id: 'quote_activity.contact_label'})}</Label>
					<ClientPrimaryContactDropdown
						clientId={clientId as string}
						value={contact}
						onChange={setContact}
						onEmpty={() => setClientHasNoContacts(true)}
						hideLabel
						dataCy="quote-decision-contact-picker"
					/>
				</FieldRow>
			)}

			{/* A blocking precondition, not a hint: the decision cannot be recorded until it is
			    resolved, so it reads as an error rather than muted guidance. */}
			{blockedMessageId && (
				<Text size="1" color="error">
					{formatMessage({id: blockedMessageId})}
				</Text>
			)}

			{copy.showsReason && (
				<ReasonFieldRow>
					<TextArea
						label={formatMessage({id: copy.reasonLabelId})}
						placeholder={formatMessage({id: copy.reasonPlaceholderId})}
						isOptional
						maxLength={MAX_REASON_LENGTH}
						value={reason}
						// TextArea's onChange receives the VALUE, not an event.
						onChange={(value: string) => setReason(value)}
						// Required by TextArea's props even though the value is already tracked on every
						// change, so there is nothing to commit on blur.
						onBlur={() => undefined}
						data-cy={copy.reasonDataCy}
					/>
				</ReasonFieldRow>
			)}

			<FlexRow gap="s" alignItems="center" justifyContent="flex-end">
				<Button onPress={onCancel} emphasis={EMPHASIS.LOW} data-cy="quote-decision-cancel">
					{formatMessage({id: 'common.cancel'})}
				</Button>
				<Button
					onPress={handleConfirm}
					disabled={!canConfirm}
					emphasis={EMPHASIS.HIGH}
					title={confirmHint}
					aria-labelledby={CONFIRM_LABEL_ID}
					data-cy="quote-decision-confirm"
				>
					<FlexRow gap="s" alignItems="center">
						<Icon icon={copy.icon} size="s" color={canConfirm ? copy.iconColor : 'disabled'} />
						<span id={CONFIRM_LABEL_ID}>{formatMessage({id: copy.confirmId})}</span>
					</FlexRow>
				</Button>
			</FlexRow>
		</DecisionPanel>
	);
};
